import { defineStore, acceptHMRUpdate } from 'pinia';
import { useUsersStore } from '@/stores/useUsers';
import { useSearchStore } from '@/stores/useSearch';
import { getDatePart } from '@/utils/date';

import router from '@/router';
import isFeatureFlagEnabled from '@/utils/featureFlags.js';
import { DeletedActivityString } from '@/utils/constants';
import { useMemoize } from '@vueuse/core';
import { useMetrics } from './useMetrics';
import { workItemApi } from '@/services/workItemService';
const ACTIVITY_ID_WIP = 2;
const ACTIVITY_ID_COMPLETE = 3;

export const useWorkItemsStore = defineStore('workItems', {
  state: () => ({
    workItems: [],
    notFound: false,
    workItem: null,
    workItemChargesTotal: 0,
    workItemPaymentsTotal: 0,
    workItemAdjustmentsTotal: 0,
    workItemActivities: [],
    workItemCharges: [],
    workItemTransactions: [],
    isLoading: true,
    isClaimStatusLoading: false,
    wipActivityLookup: [],
    completeActivityLookup: [],
    queryResults: [],
    activityDirection: 'DESC',
    activityField: 'mod_timest',
    chargesCursor: 0,
    chargesLimit: 10,
    chargesPage: 1,
    chargesDirection: 'ASC',
    chargesField: 'mod_timest',
    transactionsDirection: 'ASC',
    transactionsField: 'mod_timest',
    activityToDelete: {},
    editActivityModeEnabled: false,
  }),
  getters: {
    getWorkItemValue: state => {
      return field => {
        return state.workItem?.[field];
      };
    },
    chargesRecords: state => {
      return state.workItemCharges.length;
    },
    chargesPaginated: state => {
      const from = Number(state.chargesLimit) * Number(state.chargesPage);
      const to = from - Number(state.chargesLimit);
      return state.workItemCharges.slice(to, from);
    },
    activityRecords: state => {
      return state.workItemActivities.length;
    },
    transactionsRecords: state => {
      return state.workItemTransactions.length;
    },
  },
  actions: {
    async fetchWorkItem(id) {
      this.isLoading = true;
      try {
        this.notFound = false;

        const workItem = await workItemApi.getWorkItem(id);

        this.workItem = workItem;
        this.workItemPaymentsTotal = workItem?.payments_summary;
        this.workItemAdjustmentsTotal = workItem?.adjustments_summary;
        this.workItemChargesTotal = workItem?.charges_summary;
      } catch {
        this.notFound = true;
        this.workItem = null;
      } finally {
        this.isLoading = false;
      }
    },
    async setWorkItemStatus(workItemId, status) {
      return await workItemApi.setWorkItemStatus(workItemId, status);
    },
    async fetchActivitiesLookup() {
      const { wipResponseData, completeResponseData } =
        await memoizedFetchActivitiesLookup();

      this.wipActivityLookup = wipResponseData;
      this.completeActivityLookup = completeResponseData;
      return true;
    },
    async fetchWorkItemActivities(
      id,
      direction = 'desc',
      field = 'mod_timest'
    ) {
      const users_store = useUsersStore();
      this.isLoading = true;
      this.activityDirection = direction;
      this.activityField = field;

      try {
        const activities = await workItemApi.getWorkItemActivities({
          id,
          direction,
          field,
        });

        // determine if activity is deletable or editable by active user
        this.workItemActivities = [];
        for (const activity of activities ?? []) {
          let allowDelete = false;
          let allowEdit = false;
          // set up conditionals
          let userFromActivity = activity.auxo_user_id
            ? users_store.getUserById(activity.auxo_user_id)
            : users_store.getUserByUsername(activity.username);

          let activeUserIsAdmin = users_store.isActiveUserAdmin;

          let activeUserOwnsComment =
            userFromActivity.auxo_user_id ===
            users_store.activeUser.auxo_user_id;

          //wip,complete,note
          let isComment = [2, 3, 4].includes(activity.activity_type_id);
          let isFromToday =
            getDatePart(activity.mod_timest) ===
            getDatePart(new Date().toISOString());

          let hasBeenDeleted = activity.note === DeletedActivityString;

          if (
            isComment &&
            activeUserOwnsComment &&
            isFromToday &&
            !hasBeenDeleted
          ) {
            allowEdit = true;
          }

          if (isComment && activeUserIsAdmin && !hasBeenDeleted) {
            allowDelete = true;
          }
          this.workItemActivities.push({
            ...activity,
            showDeleteIcon: allowDelete,
            showEditIcon: allowEdit,
          });
        }
      } finally {
        this.isLoading = false;
      }
    },
    async fetchWorkItemCharges(id, direction = 'asc', field = 'mod_timest') {
      this.isLoading = true;
      this.chargesDirection = direction;
      this.chargesField = field;

      try {
        const charges = await workItemApi.getWorkItemCharges({
          id,
          direction,
          field,
        });

        this.workItemCharges = charges ?? [];
      } finally {
        this.isLoading = false;
      }
    },
    async fetchWorkItemTransactions(
      id,
      direction = 'desc',
      field = 'mod_timest'
    ) {
      this.isLoading = true;
      this.transactionDirection = direction;
      this.transactionField = field;

      try {
        const transactions = await workItemApi.getWorkItemTransactions({
          id,
          direction,
          field,
        });

        this.workItemTransactions = transactions ?? [];
        if (isFeatureFlagEnabled('HIDE_TRANSACTION_CODES')) {
          this.workItemTransactions = this.workItemTransactions.map(item => ({
            ...item,
            activity_subtype:
              item.activity_type == 'payment' ||
              item.activity_type == 'adjustment'
                ? ''
                : item.activity_subtype,
          }));
        }
      } finally {
        this.isLoading = false;
      }
    },
    async getClaimStatus() {
      const users_store = useUsersStore();
      const auxo_user_id = users_store.activeUser?.auxo_user_id;
      const response = await workItemApi.getClaimStatus({
        workItemId: this.workItem.work_item_id,
        tin: this.workItem.tax_id,
        planId: this.workItem.current_plan_id,
        lookupValue: this.workItem.claim_number,
        replyTo: auxo_user_id,
      });
      // refetch activities to see claim status
      await this.fetchWorkItemActivities(this.workItem.work_item_id);
      return response;
    },
    async postActivity(
      workItemId,
      activityId,
      activityName,
      subactivityId,
      subActivityName,
      notes,
      ua_id,
      draft_id = null
    ) {
      const users_store = useUsersStore();
      const auxo_user_id = users_store.activeUser?.auxo_user_id;

      const response = await workItemApi.postActivity({
        workItemId,
        activityId,
        activityName,
        subactivityId,
        subActivityName,
        notes,
        ua_id,
        auxo_user_id,
        draft_id,
      });
      const metrics_store = useMetrics();
      // Ignore 'Note' activities for active user metrics
      if (activityName !== 'Note')
        metrics_store.incrementActiveUserMetric(activityName);
      return response;
    },
    async editWorkItemActivity(activityId, note, updateType, draft_id = null) {
      const workItemId = this.workItem.work_item_id;

      const response = await workItemApi.editWorkItemActivity({
        workItemId,
        activityId,
        note,
        updateType,
        draft_id,
      });
      return response;
    },
    async getNoteDraft(workItemId) {
      const users_store = useUsersStore();
      const user_id = users_store.getActiveUserAttribute('auxo_user_id');

      return await workItemApi.getNoteDraft(workItemId, user_id);
    },
    async saveNoteDraft(
      workItemId,
      note,
      draftId,
      activityTypeId = null,
      activitySubtypeId = null
    ) {
      const users_store = useUsersStore();
      const auxo_user_id = users_store.activeUser?.auxo_user_id;

      // Skip saving empty drafts that might override existing ones
      if (!workItemId) {
        return null;
      }

      const response = await workItemApi.saveNoteDraft({
        workItemId,
        auxo_user_id,
        note,
        draftId,
        activityTypeId,
        activitySubtypeId,
      });
      return response;
    },
    async navigateBack({ discardSearchOrigin = false } = {}) {
      if (discardSearchOrigin) {
        // User completed work item or added to WIP so we don't
        // want the search modal to pop up
        const search_store = useSearchStore();
        search_store.setSearchOriginPath('');
      }
      const nestedRoutes = ['/document'];
      if (window.history.state.back) {
        window.history.state.back.includes(nestedRoutes)
          ? router.push('/lead')
          : router.go(-1);
      } else {
        router.push('/lead');
      }
    },
    reset() {
      this.workItem = null;
    },
  },
});

const memoizedFetchActivitiesLookup = useMemoize(async () => {
  const activityResponse = await workItemApi.getActivitySubtypes();

  let wipResponseData =
    activityResponse
      .filter(a => a.parent_type_id === ACTIVITY_ID_WIP && a.is_active === true)
      .map(a => ({
        ...a,
        value: a.name,
        key: parseInt(a.activity_subtype_id),
      })) || [];
  let completeResponseData =
    activityResponse
      .filter(
        a => a.parent_type_id === ACTIVITY_ID_COMPLETE && a.is_active === true
      )
      .map(a => ({
        ...a,
        value: a.name,
        key: parseInt(a.activity_subtype_id),
      })) || [];

  return { wipResponseData, completeResponseData };
});

// Enable Hot Module Replacement (HMR)
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useWorkItemsStore, import.meta.hot));
}
